import type { MetricDefinition } from './types';

/**
 * Local-development stand-in for `GET /api/dashboard-metrics`. The real definitions live in
 * `EPN.Dashboard.Api/Features/MetricDictionary/DashboardMetricDictionary.cs` (docs/dashboard-metric-dictionary.md); the wording of
 * `definition` and `formula` below follows it. The shape, not the prose, is what the UI depends on.
 */
const COMPANY_SCOPE = "The caller's own company, from the signed-in session only. No request carries a company id.";
const MONTH_BASIS = "The payroll run's check date falls in the reporting period (calendar month in the company time zone).";
const PRIOR_MONTH = 'The same figure for the previous calendar month. No change badge is shown when the prior month is zero or unavailable.';
const APPROVED = 'Approved payroll runs only, including every employee on them.';

function supported(
  metricId: string, displayName: string, definition: string, formula: string, extra: Partial<MetricDefinition> = {},
): MetricDefinition {
  return {
    metricId, displayName, definition, formula,
    sourceModule: 'Payroll Calculation',
    included: APPROVED,
    excluded: 'Draft, pending, rejected and withdrawn runs.',
    dateBasis: MONTH_BASIS,
    comparisonRule: PRIOR_MONTH,
    requiredPermission: 'dashboard.read',
    tenantScope: COMPANY_SCOPE,
    freshness: 'Read when the page loads; the page shows the time it was generated.',
    unavailableBehavior: 'The card shows "Data unavailable" and names the reason. It is never shown as zero.',
    availability: 'Supported',
    unavailableReason: null,
    ...extra,
  };
}

function unavailable(metricId: string, displayName: string, definition: string, reason: string): MetricDefinition {
  return supported(metricId, displayName, definition, 'Not defined: no authoritative source exists yet.', {
    included: 'n/a', excluded: 'n/a', freshness: 'n/a', availability: 'Unavailable', unavailableReason: reason,
  });
}

export const MOCK_METRIC_DICTIONARY: MetricDefinition[] = [
  supported('gross-wages', 'Gross wages', 'Everything employees earned before any tax or deduction was taken out.',
    'Sum of GrossEarnings on each approved payroll calculation. Reconciles to: net pay + employee taxes withheld + employee deductions.'),
  supported('employer-payroll-taxes', 'Employer payroll taxes',
    'Payroll taxes the company pays on top of wages, such as the employer share of Social Security and Medicare.',
    'Sum of EmployerTaxes on each approved payroll calculation.'),
  supported('employee-taxes-withheld', 'Employee taxes withheld',
    "Taxes taken out of employees' pay and sent to the tax agencies on their behalf. These are part of gross wages, not an extra company cost.",
    'Sum of EmployeeTaxes on each approved payroll calculation.'),
  supported('employee-deductions', 'Employee deductions',
    "Amounts taken out of employees' pay for benefits, retirement, garnishments and similar items. These are part of gross wages, not an extra company cost.",
    'Sum of (PreTaxDeductionsTotal + PostTaxDeductionsTotal + GarnishmentsTotal) on each approved payroll calculation.'),
  supported('net-pay', 'Net pay', 'The amount employees take home after taxes and deductions.',
    "Sum of NetPayAmount on each approved payroll run's calculations.", { sourceModule: 'Payroll Review' }),
  supported('total-employer-payroll-cost', 'Total employer payroll cost',
    'What payroll cost the company this period: wages plus the payroll taxes the company pays on them. Employer-paid benefits and payroll service fees are not included yet.',
    'Gross wages + employer payroll taxes. Employee taxes and deductions are not added again, because they are already inside gross wages.',
    {
      included: 'Gross wages and employer payroll taxes.',
      excluded: 'Employer-paid benefits and contributions and payroll service fees (no authoritative per-run source exists yet), employee deductions as an extra cost, and runs that are not approved.',
    }),
  supported('total-employees-paid', 'Employees paid',
    'How many different employees received pay on an approved payroll this period. This is not the same as the number of active employees.',
    'Count of distinct employees across the approved payroll runs in the period.', { sourceModule: 'Payroll Review' }),
  supported('average-employer-cost-per-paid-employee', 'Average employer cost per paid employee',
    'What the average employee who was paid cost the company in this period, including the payroll taxes the company pays on their wages.',
    'Total employer payroll cost / employees paid, rounded to the cent. Shown as unavailable when nobody was paid.'),
  supported('total-taxes-due', 'Taxes due this month',
    'Federal payroll tax amounts the company still owes whose due date falls in the reporting month.',
    'Sum of TaxLiability.TotalAmount for liabilities with no settled deposit and a statutory due date in the period.',
    {
      sourceModule: 'Tax Compliance',
      included: 'Unpaid Form 941 and Form 940 liabilities.',
      excluded: 'State and local taxes (no such liabilities exist yet). Due dates are statutory and are not adjusted for weekends or holidays on this card.',
    }),
  supported('deduction-elections', 'Elected deductions (current elections)',
    "What employees' active deduction elections currently say will be taken. It describes current elections, not what was withheld in a specific pay period.",
    'Sum of AmountOverride on active deduction elections for employees paid in the period, grouped by deduction code.'),
  unavailable('employer-paid-benefits', 'Employer-paid benefits and contributions',
    'What the company pays toward employee benefits such as medical, dental, vision and retirement.',
    'Payroll calculation does not yet produce employer benefit contributions per run, so there is no authoritative amount to show.'),
  unavailable('payroll-service-fees', 'Payroll service fees', 'Fees the company pays Escochex for payroll processing.',
    'No per-payroll service fee is captured yet. Subscription invoices are a separate billing record.'),
  unavailable('payroll-cost-percent-of-revenue', 'Payroll cost as a percentage of revenue',
    "Total employer payroll cost divided by the company's revenue for the same period.",
    'No accounting integration supplies revenue data.'),
  unavailable('labor-budget-variance', 'Budget variance', "How actual labor cost compares to the company's labor budget.",
    'No labor budget can be configured yet.'),
  unavailable('overtime-cost', 'Overtime hours and cost', 'Hours worked beyond the regular schedule and what they cost.',
    'Payroll calculation does not yet separate overtime from regular wages.'),
];
